import { useState, useMemo } from 'react';
import { useVouchers } from '../../hooks/useVouchers';
import { Voucher, VoucherType, VoucherStatus, voucherTypeColors, voucherTypeIcons } from '../../mocks/vouchers';
import { agencyInfo } from '../../mocks/dashboard';
import VoucherCard from './components/VoucherCard';
import VoucherModal from './components/VoucherModal';

type FilterType = VoucherType | 'All';
type FilterStatus = VoucherStatus | 'All';

export default function VouchersPage() {
  const { vouchers, loading } = useVouchers();
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [typeFilter, setTypeFilter] = useState<FilterType>('All');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('All');
  const [search, setSearch] = useState('');
  const brand = agencyInfo.brandColor;

  const types: FilterType[] = ['All', 'Hotel', 'Transfer', 'Tour', 'Activity', 'Cruise'];
  const statuses: FilterStatus[] = ['All', 'Active', 'Used', 'Cancelled', 'Expired'];

  const filtered = useMemo(() => {
    return vouchers.filter((v) => {
      const matchType = typeFilter === 'All' || v.type === typeFilter;
      const matchStatus = statusFilter === 'All' || v.status === statusFilter;
      const matchSearch =
        !search ||
        v.number.toLowerCase().includes(search.toLowerCase()) ||
        v.customer.toLowerCase().includes(search.toLowerCase()) ||
        v.destination.toLowerCase().includes(search.toLowerCase()) ||
        v.supplierName.toLowerCase().includes(search.toLowerCase()) ||
        v.bookingId.toLowerCase().includes(search.toLowerCase());
      return matchType && matchStatus && matchSearch;
    });
  }, [vouchers, typeFilter, statusFilter, search]);

  const kpis = [
    { label: 'Total Vouchers', value: vouchers.length, icon: 'ri-coupon-3-line', color: brand },
    { label: 'Active', value: vouchers.filter((v) => v.status === 'Active').length, icon: 'ri-check-double-line', color: '#10B981' },
    { label: 'Used', value: vouchers.filter((v) => v.status === 'Used').length, icon: 'ri-archive-line', color: '#6B7280' },
    { label: 'Cancelled', value: vouchers.filter((v) => v.status === 'Cancelled').length, icon: 'ri-close-circle-line', color: '#EF4444' },
  ];

  return (
    <div className="min-h-screen bg-gray-50/50 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Vouchers</h1>
          <p className="text-sm text-gray-500 mt-0.5">Generate and manage hotel, transfer, and tour service vouchers</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            <i className="ri-download-2-line" />
            Export All
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-add-line" />
            New Voucher
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-5 mb-8">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4" style={{ backgroundColor: `${kpi.color}15` }}>
              <i className={`${kpi.icon} text-lg`} style={{ color: kpi.color }} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            <p className="text-sm font-medium text-gray-600 mt-1">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search voucher #, customer, destination..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none"
            />
          </div>

          {/* Type Filter */}
          <div className="flex gap-1.5 flex-wrap">
            {types.map((t) => {
              const active = typeFilter === t;
              const color = t !== 'All' ? voucherTypeColors[t as VoucherType] : brand;
              const icon = t !== 'All' ? voucherTypeIcons[t as VoucherType] : 'ri-apps-line';
              return (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                    active ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={active ? { backgroundColor: color } : {}}
                >
                  <i className={`${icon} text-xs`} />
                  {t}
                </button>
              );
            })}
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FilterStatus)}
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer"
          >
            {statuses.map((s) => (
              <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Showing <span className="font-semibold text-gray-700">{filtered.length}</span> of {vouchers.length} vouchers
        </p>
      </div>

      {/* Voucher Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((voucher) => (
            <VoucherCard key={voucher.id} voucher={voucher} onView={setSelectedVoucher} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 py-20 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4" style={{ backgroundColor: `${brand}10` }}>
            <i className="ri-coupon-3-line text-2xl" style={{ color: brand }} />
          </div>
          <p className="font-semibold text-gray-700 mb-1">No vouchers found</p>
          <p className="text-sm text-gray-400">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Voucher Modal */}
      {selectedVoucher && (
        <VoucherModal voucher={selectedVoucher} onClose={() => setSelectedVoucher(null)} />
      )}
    </div>
  );
}
